// The browser already holds the files the user picked. Showing them (thumbnails, the right-hand viewer) from here
// needs no request to Root, so it works only while this page is open: after a reload or from History there is
// nothing here and callers fall back to the plain file card.
const entries = new Map();
let version = 0;
const listeners = new Set();
const bump = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};
export const subscribeLocalFiles = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const localFilesVersion = () => version;

const makeUrl = (file) => {
  try {
    return typeof URL.createObjectURL === "function"
      ? URL.createObjectURL(file)
      : null;
  } catch (error) {
    return null;
  }
};
const revoke = (entry) => {
  try {
    if (entry?.url) URL.revokeObjectURL(entry.url);
  } catch (error) {
    // nothing to release
  }
};

export function keepLocalFile(id, sessionId, file) {
  revoke(entries.get(id));
  const url = makeUrl(file);
  if (url)
    entries.set(id, { sessionId, url, mimeType: file.type, name: file.name });
  else entries.delete(id);
  bump();
}
export const localFile = (id) => entries.get(id) || null;
export function dropLocalFile(id) {
  revoke(entries.get(id));
  entries.delete(id);
  bump();
}
export function dropLocalFilesOutside(sessionId) {
  [...entries].forEach(([id, entry]) => {
    if (entry.sessionId !== sessionId) dropLocalFile(id);
  });
}

// A conversation reopened from History holds none of its files. They are fetched from Root when something needs them
// (a thumbnail on screen, a citation or file card clicked), once each, and then behave like files picked on this page.
// A fetch that fails (expired, removed, not theirs) is not repeated until the conversation changes. Reading a file
// renews its idle timer on the server, so nothing is fetched just because a conversation was opened.
let loader = null;
let loaderSession = null;
const inflight = new Map();
const failed = new Set();
export const hasOriginalLoader = () => !!loader;
export function setOriginalLoader(sessionId, load) {
  loader = load;
  loaderSession = sessionId;
  failed.clear();
}
export async function ensureLocalFile(id) {
  const held = entries.get(id);
  if (held) return held;
  if (!loader || failed.has(id)) return null;
  if (!inflight.has(id)) {
    const session = loaderSession;
    inflight.set(
      id,
      loader(id)
        .then((file) => {
          if (session !== loaderSession) return null;
          keepLocalFile(id, session, file);
          return entries.get(id) || null;
        })
        .catch(() => {
          failed.add(id);
          return null;
        })
        .finally(() => inflight.delete(id)),
    );
  }
  return inflight.get(id);
}
